/**
 * Admin → Kiosks tab.
 *
 * Lets the owner register a kiosk (gets a one-shot device_token they paste
 * into the kiosk's /kiosk?setup=1 page), see lifetime stats + recent deposits,
 * rotate the token (e.g., when a kiosk PC is replaced/stolen), and toggle
 * min/max amounts or active status.
 *
 * The token is only ever shown ONCE — at creation and at rotation — so we
 * surface it in a dialog with a copy button + a clear "won't be shown again"
 * warning. After dismiss, only stats remain visible.
 */

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Banknote, Plus, Copy, RefreshCw, ShieldAlert, Power, Pencil, CheckCircle2,
    ExternalLink,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch { return '—'; }
};

const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export const KioskAdminPanel = () => {
    const [kiosks, setKiosks] = useState([]);
    const [deposits, setDeposits] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create / rotate dialog state
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ label: '', location: '', min_amount: 5, max_amount: 500 });
    const [creating, setCreating] = useState(false);
    const [newToken, setNewToken] = useState(null);   // { label, device_token, rotated: bool }

    // Edit dialog state
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const loadAll = useCallback(async () => {
        try {
            const [k, d] = await Promise.all([
                axios.get(`${API}/admin/kiosks`, { withCredentials: true }),
                axios.get(`${API}/admin/kiosks/deposits?limit=50`, { withCredentials: true }),
            ]);
            setKiosks(k.data?.kiosks || []);
            setDeposits(d.data?.deposits || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load kiosks');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    // ───────── Create ─────────
    const handleCreate = async () => {
        const { label, location, min_amount, max_amount } = createForm;
        if (!label.trim()) { toast.error('Label is required'); return; }
        if (Number(min_amount) < 1 || Number(max_amount) < Number(min_amount)) {
            toast.error('Min ≥ $1 and max ≥ min'); return;
        }
        setCreating(true);
        try {
            const { data } = await axios.post(`${API}/admin/kiosks`, {
                label: label.trim(), location: location.trim(),
                min_amount: Number(min_amount), max_amount: Number(max_amount),
            }, { withCredentials: true });
            setNewToken({ label: data.label, device_token: data.device_token, rotated: false });
            setShowCreate(false);
            setCreateForm({ label: '', location: '', min_amount: 5, max_amount: 500 });
            await loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create kiosk');
        } finally {
            setCreating(false);
        }
    };

    // ───────── Rotate ─────────
    const handleRotate = async (kiosk) => {
        if (!window.confirm(`Rotate the device token for "${kiosk.label}"?\n\nThe current kiosk PC will stop accepting cash until you paste the new token into /kiosk?setup=1 on that device.`)) return;
        try {
            const { data } = await axios.post(`${API}/admin/kiosks/${kiosk.id}/rotate-token`, {}, { withCredentials: true });
            setNewToken({ label: kiosk.label, device_token: data.device_token, rotated: true });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to rotate token');
        }
    };

    // ───────── Edit ─────────
    const openEdit = (k) => {
        setEditing(k);
        setEditForm({
            label: k.label, location: k.location || '',
            min_amount: k.min_amount, max_amount: k.max_amount,
            active: k.active,
        });
    };

    const handleSaveEdit = async () => {
        if (!editing) return;
        if (!editForm.label?.trim()) { toast.error('Label is required'); return; }
        if (Number(editForm.min_amount) < 1 || Number(editForm.max_amount) < Number(editForm.min_amount)) {
            toast.error('Min ≥ $1 and max ≥ min'); return;
        }
        setSaving(true);
        try {
            await axios.patch(`${API}/admin/kiosks/${editing.id}`, {
                label: editForm.label.trim(),
                location: (editForm.location || '').trim(),
                min_amount: Number(editForm.min_amount),
                max_amount: Number(editForm.max_amount),
                active: !!editForm.active,
            }, { withCredentials: true });
            toast.success('Kiosk updated');
            setEditing(null);
            await loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update kiosk');
        } finally {
            setSaving(false);
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied');
        } catch {
            toast.error('Copy failed — long-press to select');
        }
    };

    // ───────── Today's reconciliation total ─────────
    // Sum deposits where created_at >= midnight Central Time today.
    const todaysTotal = (() => {
        try {
            const ct = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
            const todayCT = ct.format(new Date());
            return deposits
                .filter((d) => d.created_at && ct.format(new Date(d.created_at)) === todayCT)
                .reduce((sum, d) => sum + Number(d.amount || 0), 0);
        } catch {
            return 0;
        }
    })();

    return (
        <div className="space-y-6" data-testid="kiosk-admin-panel">
            {/* Header + summary */}
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-2xl font-black text-white flex items-center gap-2">
                        <Banknote className="w-6 h-6 text-[#FFD700]" /> Kiosks
                    </h2>
                    <p className="text-[#A0AAB2] text-sm mt-1">
                        Cash-loading terminals. Optional — players can still load via the usual top-up flow.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-[#A0AAB2]">Today (CT)</p>
                        <p className="text-2xl font-mono font-bold text-[#39FF14]" data-testid="kiosk-todays-total">
                            {fmtMoney(todaysTotal)}
                        </p>
                    </div>
                    <Button
                        onClick={() => setShowCreate(true)}
                        className="bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                        data-testid="kiosk-create-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> New Kiosk
                    </Button>
                </div>
            </div>

            {/* Kiosk list */}
            {loading ? (
                <p className="text-[#A0AAB2] text-sm">Loading…</p>
            ) : kiosks.length === 0 ? (
                <div className="card-dark p-8 text-center" data-testid="kiosk-empty-state">
                    <Banknote className="w-12 h-12 text-[#A0AAB2] mx-auto mb-3" />
                    <p className="text-white font-bold">No kiosks yet</p>
                    <p className="text-[#A0AAB2] text-sm mt-1 mb-4">
                        Add your first kiosk, copy the device token, then paste it into <span className="text-white font-mono">/kiosk?setup=1</span> on the kiosk PC.
                    </p>
                    <Button onClick={() => setShowCreate(true)} className="bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold">
                        <Plus className="w-4 h-4 mr-1" /> Register Kiosk
                    </Button>
                </div>
            ) : (
                <div className="grid gap-3">
                    {kiosks.map((k) => (
                        <div key={k.id} className="card-dark p-4" data-testid={`kiosk-row-${k.id}`}>
                            <div className="flex items-start justify-between flex-wrap gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-lg font-bold text-white">{k.label}</h3>
                                        {!k.active && (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/40">
                                                Inactive
                                            </span>
                                        )}
                                        {k.active && (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40">
                                                Active
                                            </span>
                                        )}
                                    </div>
                                    {k.location && <p className="text-[#A0AAB2] text-sm">{k.location}</p>}
                                    <p className="text-[#A0AAB2] text-xs mt-1">
                                        Min {fmtMoney(k.min_amount)} · Max {fmtMoney(k.max_amount)} ·
                                        Last deposit {fmtCT(k.last_deposit_at)}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-mono font-bold text-[#FFD700]">{fmtMoney(k.total_amount)}</p>
                                    <p className="text-[#A0AAB2] text-[11px]">{k.total_deposits} deposit{k.total_deposits === 1 ? '' : 's'}</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Button
                                    onClick={() => openEdit(k)}
                                    variant="outline"
                                    className="border-white/20 text-white hover:bg-white/5"
                                    data-testid={`kiosk-edit-${k.id}`}
                                >
                                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                                </Button>
                                <Button
                                    onClick={() => handleRotate(k)}
                                    variant="outline"
                                    className="border-[#FF8C00]/40 text-[#FF8C00] hover:bg-[#FF8C00]/10"
                                    data-testid={`kiosk-rotate-${k.id}`}
                                >
                                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rotate Token
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Recent deposits feed */}
            <div className="card-dark p-4">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-white">Recent Cash-Ins</h3>
                    <Button
                        onClick={loadAll}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/5"
                        data-testid="kiosk-refresh-btn"
                    >
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                    </Button>
                </div>
                {deposits.length === 0 ? (
                    <p className="text-[#A0AAB2] text-sm text-center py-6">No kiosk deposits yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[#A0AAB2] text-xs uppercase border-b border-white/10">
                                    <th className="py-2 pr-3">When (CT)</th>
                                    <th className="py-2 pr-3">Kiosk</th>
                                    <th className="py-2 pr-3">Player</th>
                                    <th className="py-2 pr-3 text-right">Amount</th>
                                    <th className="py-2 text-right">Bal After</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deposits.map((d) => (
                                    <tr key={d.id} className="border-b border-white/5" data-testid={`kiosk-deposit-row-${d.id}`}>
                                        <td className="py-2 pr-3 text-[#A0AAB2]">{fmtCT(d.created_at)}</td>
                                        <td className="py-2 pr-3 text-white">{d.kiosk_label || '—'}</td>
                                        <td className="py-2 pr-3 text-white">{d.user_name || '—'}<span className="text-[#A0AAB2] text-xs ml-1">{d.user_phone}</span></td>
                                        <td className="py-2 pr-3 text-right text-[#39FF14] font-mono font-bold">{fmtMoney(d.amount)}</td>
                                        <td className="py-2 text-right text-[#FFD700] font-mono">{fmtMoney(d.balance_after)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ───────── Create dialog ───────── */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Banknote className="w-5 h-5 text-[#FFD700]" /> Register New Kiosk
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Give the kiosk a name and amount limits. You'll get a one-shot device token to paste into the kiosk PC.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white">Label</Label>
                            <Input
                                value={createForm.label}
                                onChange={(e) => setCreateForm((f) => ({ ...f, label: e.target.value }))}
                                placeholder="e.g. Main Store Kiosk"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="kiosk-create-label"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-white">Location <span className="text-[#A0AAB2] text-xs">(optional)</span></Label>
                            <Input
                                value={createForm.location}
                                onChange={(e) => setCreateForm((f) => ({ ...f, location: e.target.value }))}
                                placeholder="e.g. Front counter"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="kiosk-create-location"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-white">Min $/session</Label>
                                <Input
                                    type="number" min="1" step="1"
                                    value={createForm.min_amount}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, min_amount: e.target.value }))}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="kiosk-create-min"
                                />
                            </div>
                            <div>
                                <Label className="text-white">Max $/session</Label>
                                <Input
                                    type="number" min="1" step="1"
                                    value={createForm.max_amount}
                                    onChange={(e) => setCreateForm((f) => ({ ...f, max_amount: e.target.value }))}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="kiosk-create-max"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleCreate}
                            disabled={creating || !createForm.label.trim()}
                            className="w-full bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                            data-testid="kiosk-create-submit"
                        >
                            {creating ? 'Creating…' : 'Create & Get Token'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ───────── New / Rotated token dialog (shown ONCE) ───────── */}
            <Dialog open={!!newToken} onOpenChange={(open) => !open && setNewToken(null)}>
                <DialogContent className="bg-[#121212] border-[#FFD700]/30">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 text-[#39FF14]" />
                            {newToken?.rotated ? 'Token Rotated' : 'Kiosk Created'}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            {newToken?.label} — copy this token now. <span className="text-[#FF8C00] font-bold">It won't be shown again.</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-[#0a0a0a] p-3 rounded-lg border border-[#FFD700]/30 font-mono text-xs text-white break-all" data-testid="kiosk-new-token">
                        {newToken?.device_token}
                    </div>
                    <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/30 p-3 rounded-lg text-[#FF8C00] text-xs flex items-start gap-2">
                        <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <p>On the kiosk PC, open Firefox → <span className="font-mono">/kiosk?setup=1</span> → paste this token → tap "Save & Lock In". The kiosk will then accept cash.</p>
                    </div>
                    <DialogFooter className="gap-2 flex-row">
                        <Button
                            onClick={() => copy(newToken?.device_token || '')}
                            className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                            data-testid="kiosk-new-token-copy"
                        >
                            <Copy className="w-4 h-4 mr-1" /> Copy Token
                        </Button>
                        <Button
                            onClick={() => setNewToken(null)}
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/5"
                            data-testid="kiosk-new-token-close"
                        >
                            Done
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ───────── Edit dialog ───────── */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Edit Kiosk</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white">Label</Label>
                            <Input
                                value={editForm.label || ''}
                                onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="kiosk-edit-label"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Location</Label>
                            <Input
                                value={editForm.location || ''}
                                onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="kiosk-edit-location"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-white">Min $</Label>
                                <Input
                                    type="number" min="1"
                                    value={editForm.min_amount ?? ''}
                                    onChange={(e) => setEditForm((f) => ({ ...f, min_amount: e.target.value }))}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="kiosk-edit-min"
                                />
                            </div>
                            <div>
                                <Label className="text-white">Max $</Label>
                                <Input
                                    type="number" min="1"
                                    value={editForm.max_amount ?? ''}
                                    onChange={(e) => setEditForm((f) => ({ ...f, max_amount: e.target.value }))}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="kiosk-edit-max"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-[#0a0a0a] border border-white/10">
                            <div className="flex items-center gap-2">
                                <Power className="w-4 h-4 text-[#A0AAB2]" />
                                <span className="text-white text-sm">Active</span>
                            </div>
                            <Switch
                                checked={!!editForm.active}
                                onCheckedChange={(v) => setEditForm((f) => ({ ...f, active: v }))}
                                data-testid="kiosk-edit-active"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleSaveEdit}
                            disabled={saving}
                            className="w-full bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold"
                            data-testid="kiosk-edit-save"
                        >
                            {saving ? 'Saving…' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quick test link */}
            <div className="text-center">
                <a
                    href="/kiosk"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-[#A0AAB2] hover:text-[#FFD700]"
                    data-testid="kiosk-open-link"
                >
                    Open kiosk UI in a new tab <ExternalLink className="w-3 h-3" />
                </a>
            </div>
        </div>
    );
};

export default KioskAdminPanel;
